import React, { useState, useEffect, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import Select from 'react-select';
import 'react-select/dist/react-select.css';
import Swal from 'sweetalert2';
import { FormattedMessage, useIntl } from 'react-intl';
import DropzoneComponent from 'react-dropzone-component/dist/react-dropzone';
import 'react-dropzone-component/styles/filepicker.css';
import 'dropzone/dist/min/dropzone.min.css';
import '../../assets/css/vec-dropzone.css';
import Config from '../../commons/config/Config.js';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import * as Utils from '../../commons/utils/Utils';
import Loading from '../ui/Loading.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import { formatearFechaHoraTurno } from '../correctivos/fechaHoraTurnoAlta';
import Switch from 'react-switch';
import { etiquetaLlavesEnVigilancia, llavesEnVigilanciaHabilitada } from './solicitudEstado.js';

const INMOVILIZADO_SI = 1;
const INMOVILIZADO_NO = 2;
const MAXIMO_ADJUNTOS = 3;
const EXTENSIONES_PERMITIDAS = '.jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.doc,.docx';

const MAXIMO_DETALLE = 240;

const formDataInicial = {
  movil: null,
  activoElegido: null,
  inmovilizado: null,
  llavesEnVigilancia: false,
  detalle: '',
  fechaHoraTurno: null,
};

const descripcionActivo = (activo) => {
  const marcaModelo = [activo.marca, activo.modelo].filter(Boolean).join(' ');
  return marcaModelo ? activo.dominio + ' — ' + marcaModelo : activo.dominio;
};

const SolicitudesAbm = () => {
  const intl = useIntl();
  const dropzone = useRef(null);
  const cantidadAdjuntos = useRef(0);
  const [redirectTo, setRedirectTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(formDataInicial);
  const [adjuntos, setAdjuntos] = useState([]);
  const [asociados, setAsociados] = useState({ estado: 'sin_movil', lista: [] });
  const movilId = formData.movil ? formData.movil.id : null;
  const inmovilizadoHabilitado = ConfigBusiness.get('tickets.inmovilizado.habilitado') === 'true';
  const fechaTurnoHabilitada = ConfigBusiness.get('mantenimientos.correctivo.fechaTurno.habilitado') === 'true'
    && ConfigBusiness.get('mantenimientos.correctivo.gerenciadores.habilitado') === 'true'
    && ConfigBusiness.get('tickets.motivoAsignacion.habilitado') !== 'true';

  useEffect(() => {
    if (ConfigBusiness.get('mantenimientos.solicitudes.habilitado') !== 'true' || !Security.hasPermission('SOLICITUDES_CREAR')) {
      setRedirectTo('/error');
    }
  }, []);

  // VEC-4528: si el móvil tiene activos asociados, se elige sobre cuál se genera la solicitud.
  useEffect(() => {
    if (movilId && ConfigBusiness.get('moviles.vinculacionActivos.habilitado') !== 'true') {
      setAsociados({ estado: 'listo', lista: [] });
      return undefined;
    }
    setAsociados({ estado: movilId ? 'cargando' : 'sin_movil', lista: [] });
    if (!movilId) return undefined;

    let vigente = true;
    Utils.getDataNoErrorMessage('/solicitudes/activos-asociados?movil=' + movilId)
      .then((lista) => {
        if (vigente) setAsociados({ estado: 'listo', lista: lista || [] });
      })
      .catch(() => {
        if (vigente) setAsociados({ estado: 'error', lista: [] });
      });

    return () => {
      vigente = false;
    };
  }, [movilId]);

  const principal = formData.movil
    ? {
        id: formData.movil.id,
        dominio: formData.movil.dominio,
        marca: formData.movil.modelo && formData.movil.modelo.marca ? formData.movil.modelo.marca.nombre : null,
        modelo: formData.movil.modelo ? formData.movil.modelo.nombre : null,
      }
    : null;
  const tieneAsociados = asociados.estado === 'listo' && asociados.lista.length > 0;

  const inmovilizadoOptions = [
    { value: INMOVILIZADO_NO, label: intl.formatMessage({ id: 'correctivosAbm.inmovilizadoOptions.no', defaultMessage: 'NO' }) },
    { value: INMOVILIZADO_SI, label: intl.formatMessage({ id: 'correctivosAbm.inmovilizadoOptions.si', defaultMessage: 'SI' }) },
  ];

  const buscarMoviles = (search) =>
    Utils.getData('/moviles/simple-search?search=' + encodeURIComponent(search || ''))
      .then((moviles) => ({ options: moviles, complete: true }))
      .catch(() => ({ options: [], complete: true }));

  const dropzoneConfig = {
    showFiletypeIcon: false,
    postUrl: Config.get('apiUrlBase') + '/adjuntos',
  };

  const mensajeTipoNoPermitido = intl.formatMessage({ id: 'solicitudesAbm.adjuntos.tipo_no_permitido', defaultMessage: 'Tipo de archivo no permitido. Se aceptan imágenes, PDF y Word.' });
  const mensajeMaximo = intl.formatMessage({ id: 'solicitudesAbm.adjuntos.maximo', defaultMessage: 'Se pueden adjuntar como máximo {maximo} archivos.' }, { maximo: MAXIMO_ADJUNTOS });

  const dropzoneDjsConfig = {
    addRemoveLinks: false,
    uploadMultiple: false,
    parallelUploads: 1,
    maxFilesize: 20,
    acceptedFiles: EXTENSIONES_PERMITIDAS,
    dictInvalidFileType: mensajeTipoNoPermitido,
    dictDefaultMessage: intl.formatMessage(
      { id: 'solicitudesAbm.adjuntos.dropzone_maximo', defaultMessage: 'Haga click o arrastre hasta {maximo} archivos (imágenes, PDF o Word). Tamaño máximo por archivo 20MB.' },
      { maximo: MAXIMO_ADJUNTOS }
    ),
    headers: { 'Authorization-Token': localStorage.getItem('token') },
    accept: (file, done) => {
      if (cantidadAdjuntos.current >= MAXIMO_ADJUNTOS) {
        done(mensajeMaximo);
        return;
      }
      cantidadAdjuntos.current += 1;
      file.contado = true;
      done();
    },
  };

  const dropzoneEventHandlers = {
    init: (dz) => {
      dropzone.current = dz;
    },
    success: (file, response) => {
      dropzone.current.removeFile(file);
      setAdjuntos((actuales) => actuales.concat([{ id: response.id, nombre: file.name }]));
    },
    error: (file, mensaje) => {
      if (file.contado) {
        cantidadAdjuntos.current -= 1;
      }
      dropzone.current.removeFile(file);
      Swal.fire({
        icon: 'error',
        title: mensaje === mensajeTipoNoPermitido || mensaje === mensajeMaximo
          ? mensaje
          : intl.formatMessage({ id: 'solicitudesAbm.adjuntos.error', defaultMessage: 'No se pudo subir el archivo' }),
        confirmButtonText: 'OK',
      });
    },
  };

  const quitarAdjunto = (id) => {
    cantidadAdjuntos.current -= 1;
    setAdjuntos((actuales) => actuales.filter((adjunto) => adjunto.id !== id));
  };

  const formularioCompleto = Boolean(formData.movil)
    && asociados.estado === 'listo'
    && (!tieneAsociados || formData.activoElegido !== null)
    && formData.detalle.trim() !== ''
    && (!inmovilizadoHabilitado || Boolean(formData.inmovilizado));

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formularioCompleto) return;

    setLoading(true);
    const datos = {
      movil: formData.movil.id,
      activoAsociado: formData.activoElegido !== null && formData.activoElegido !== formData.movil.id ? formData.activoElegido : null,
      detalle: formData.detalle,
      llavesEnVigilancia: formData.llavesEnVigilancia,
      inmovilizado: inmovilizadoHabilitado ? formData.inmovilizado.value === INMOVILIZADO_SI : null,
      adjuntos: adjuntos.map((adjunto) => adjunto.id),
      fechaHoraTurno: fechaTurnoHabilitada ? formatearFechaHoraTurno(formData.fechaHoraTurno) : null,
    };

    Utils.sendData('/solicitudes', 'POST', datos)
      .then((solicitud) => {
        if (!solicitud) return;
        Swal.fire({
          icon: 'success',
          title: intl.formatMessage({ id: 'solicitudesAbm.guardada.titulo', defaultMessage: 'Solicitud guardada' }),
          text: intl.formatMessage({ id: 'solicitudesAbm.guardada.texto', defaultMessage: 'Se generó la solicitud {numero}.' }, { numero: solicitud.numero }),
          confirmButtonText: 'OK',
        }).then(() => setRedirectTo('/solicitudes'));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  if (redirectTo) return <Redirect push to={redirectTo} />;

  return (
    <React.Fragment>
      {loading && <Loading />}
      <form className="form form-horizontal" onSubmit={handleSubmit}>
        <div className="form-body">
          <div className="card pull-up">
            <div className="card-content">
              <div className="card-body">
                <h4 className="form-section">
                  <i className="la la-file-text"></i>
                  <FormattedMessage id="solicitudesAbm.titulo" defaultMessage="Nueva Solicitud" />
                </h4>

                <div className="form-group row">
                  <label className="col-md-3 label-control col-form-label" htmlFor="movil">
                    <FormattedMessage id="solicitudesAbm.entidad" defaultMessage="Entidad" /> *:
                  </label>
                  <div className="col-md-9">
                    <Select.Async
                      id="movil"
                      name="movil"
                      value={formData.movil}
                      valueKey="id"
                      labelKey="dominio"
                      loadOptions={buscarMoviles}
                      cache={false}
                      onChange={(movil) => setFormData({ ...formData, movil: movil, activoElegido: null })}
                      placeholder={intl.formatMessage({ id: 'solicitudesAbm.entidad.placeholder', defaultMessage: 'Dominio' })}
                    />
                  </div>
                </div>

                {formData.movil && (asociados.estado !== 'listo' || tieneAsociados) && (
                  <div className="form-group row">
                    <label className="col-md-3 label-control col-form-label">
                      <FormattedMessage id="solicitudesAbm.activo_asociado" defaultMessage="Activo asociado" /> *:
                    </label>
                    <div className="col-md-9">
                      {asociados.estado === 'cargando' && (
                        <div className="col-form-label text-muted">
                          <i className="la la-spinner spinner"></i>{' '}
                          <FormattedMessage id="solicitudesAbm.activo_asociado.buscando" defaultMessage="Buscando activos asociados…" />
                        </div>
                      )}
                      {asociados.estado === 'error' && (
                        <div className="col-form-label text-danger">
                          <i className="la la-exclamation-circle"></i>{' '}
                          <FormattedMessage
                            id="solicitudesAbm.activo_asociado.error"
                            defaultMessage="No se pudieron consultar los activos asociados. Quite el dominio y vuelva a seleccionarlo para reintentar."
                          />
                        </div>
                      )}
                      {tieneAsociados && (
                        <React.Fragment>
                          <div className="col-form-label">
                            <FormattedMessage
                              id="solicitudesAbm.activo_asociado.instruccion"
                              defaultMessage="El activo {dominio} tiene activos asociados. Seleccione sobre cuál se genera la solicitud:"
                              values={{ dominio: principal.dominio }}
                            />
                          </div>
                          {[principal].concat(asociados.lista).map((activo) => (
                            <div key={activo.id} className="form-check mb-1">
                              <input
                                type="radio"
                                className="form-check-input"
                                id={'activoElegido' + activo.id}
                                name="activoElegido"
                                checked={formData.activoElegido === activo.id}
                                onChange={() => setFormData({ ...formData, activoElegido: activo.id })}
                              />
                              <label className="form-check-label" htmlFor={'activoElegido' + activo.id}>
                                {descripcionActivo(activo)}{' '}
                                <span className="text-muted">
                                  {activo.id === principal.id
                                    ? intl.formatMessage({ id: 'solicitudesAbm.activo_asociado.principal', defaultMessage: '(activo principal)' })
                                    : intl.formatMessage({ id: 'solicitudesAbm.activo_asociado.asociado', defaultMessage: '(activo asociado)' })}
                                </span>
                              </label>
                            </div>
                          ))}
                          {formData.activoElegido === null && (
                            <small className="text-muted">
                              <FormattedMessage
                                id="solicitudesAbm.activo_asociado.ayuda"
                                defaultMessage="Seleccione un activo asociado o confirme el activo principal para poder guardar."
                              />
                            </small>
                          )}
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-group row">
                  <label className="col-md-3 label-control col-form-label" htmlFor="unidad">
                    <FormattedMessage id="solicitudesAbm.unidad" defaultMessage="Unidad" />:
                  </label>
                  <div className="col-md-9">
                    <input id="unidad" type="text" className="form-control" value={(formData.movil && formData.movil.unidad_original) || ''} disabled />
                  </div>
                </div>

                {llavesEnVigilanciaHabilitada() && (
                  <div className="form-group row">
                    <label className="col-md-3 label-control col-form-label" id="llavesEnVigilanciaLabel">
                      <FormattedMessage id="solicitudes.llaves_en_vigilancia" defaultMessage="Llaves en vigilancia" /> *:
                    </label>
                    <div className="col-md-9 d-flex align-items-center">
                      <Switch
                        id="llavesEnVigilancia"
                        aria-labelledby="llavesEnVigilanciaLabel"
                        checked={formData.llavesEnVigilancia}
                        onChange={(valor) => setFormData({ ...formData, llavesEnVigilancia: valor })}
                        offColor="#FF4961"
                        onColor="#28D094"
                      />
                      <span className="ml-1">{etiquetaLlavesEnVigilancia(intl, formData.llavesEnVigilancia)}</span>
                    </div>
                  </div>
                )}

                {(inmovilizadoHabilitado || fechaTurnoHabilitada) && (
                  <div className="row">
                    {inmovilizadoHabilitado && (
                      <div className="col-12 col-md-6">
                        <div className="form-group row">
                          <label className="col-md-6 label-control col-form-label" htmlFor="inmovilizado">
                            <FormattedMessage id="correctivosAbm.datos_generales.inmovilizado" defaultMessage="Inmovilizado" /> *:
                          </label>
                          <div className="col-md-6">
                            <Select
                              id="inmovilizado"
                              name="inmovilizado"
                              options={inmovilizadoOptions}
                              value={formData.inmovilizado}
                              valueKey="value"
                              labelKey="label"
                              clearable={false}
                              onChange={(opcion) => setFormData({ ...formData, inmovilizado: opcion })}
                              placeholder={intl.formatMessage({ id: 'solicitudesAbm.inmovilizado.placeholder', defaultMessage: 'Seleccione' })}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {fechaTurnoHabilitada && (
                      <div className="col-12 col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="fechaHoraTurno">
                            <FormattedMessage id="solicitudesAbm.fecha_turno" defaultMessage="Fecha de Turno" />:
                          </label>
                          <div className="col-md-8">
                            <DatePicker
                              id="fechaHoraTurno"
                              name="fechaHoraTurno"
                              className="form-control date-picker-placeholder"
                              dateFormat={LocaleFormat.getDateTimePickerFormat()}
                              placeholderText={intl.formatMessage({ id: 'solicitudesAbm.fecha_turno.placeholder', defaultMessage: 'DD/MM/AAAA' })}
                              selected={formData.fechaHoraTurno ? moment(formData.fechaHoraTurno) : null}
                              onChange={(fecha) => setFormData({ ...formData, fechaHoraTurno: fecha ? fecha.format() : null })}
                              minDate={moment()}
                              showTimeSelect
                              timeIntervals={10}
                              popperClassName="datepicker-popper-con-hora"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group row">
                  <label className="col-md-3 label-control col-form-label" htmlFor="detalle">
                    <FormattedMessage id="solicitudesAbm.detalle" defaultMessage="Detalle" /> *:
                  </label>
                  <div className="col-md-9">
                    <textarea
                      id="detalle"
                      name="detalle"
                      className="form-control"
                      rows="5"
                      maxLength={MAXIMO_DETALLE}
                      value={formData.detalle}
                      onChange={(event) => setFormData({ ...formData, detalle: event.target.value })}
                      placeholder={intl.formatMessage({ id: 'solicitudesAbm.detalle.placeholder', defaultMessage: 'Describa el problema' })}
                    />
                    <small className="text-muted float-right">
                      {formData.detalle.length} / {MAXIMO_DETALLE}
                    </small>
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-md-3 label-control col-form-label">
                    <FormattedMessage id="solicitudesAbm.adjuntos" defaultMessage="Fotos y adjuntos" />:
                  </label>
                  <div className="col-md-9">
                    <div style={{ display: adjuntos.length >= MAXIMO_ADJUNTOS ? 'none' : 'block' }}>
                      <DropzoneComponent config={dropzoneConfig} djsConfig={dropzoneDjsConfig} eventHandlers={dropzoneEventHandlers} />
                    </div>
                    {adjuntos.length >= MAXIMO_ADJUNTOS && (
                      <div className="text-muted">
                        <FormattedMessage
                          id="solicitudesAbm.adjuntos.maximo_alcanzado"
                          defaultMessage="Se alcanzó el máximo de {maximo} archivos. Quite uno para adjuntar otro."
                          values={{ maximo: MAXIMO_ADJUNTOS }}
                        />
                      </div>
                    )}
                    {adjuntos.map((adjunto) => (
                      <div key={adjunto.id} className="mt-1">
                        <i className="la la-paperclip"></i> {adjunto.nombre}
                        <button type="button" className="btn btn-sm btn-link text-danger" onClick={() => quitarAdjunto(adjunto.id)}>
                          <i className="la la-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="alert alert-info mb-0">
                  <FormattedMessage
                    id="solicitudesAbm.aviso_revision"
                    defaultMessage="Tu solicitud la revisa el equipo de mantenimiento. Cuando se genere el ticket o se rechace, te llega una notificación."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card pull-up">
            <div className="card-content">
              <div className="card-body">
                <div className="text-cd text-right">
                  <button type="submit" className="btn btn-primary mr-1" disabled={loading || !formularioCompleto}>
                    <i className="fa fa-check-circle"></i> <FormattedMessage id="solicitudesAbm.guardar" defaultMessage="Guardar" />
                  </button>
                  <button type="button" className="btn btn-danger" onClick={() => setRedirectTo('/solicitudes')}>
                    <i className="fa fa-times-circle"></i> <FormattedMessage id="solicitudesAbm.cancelar" defaultMessage="Cancelar" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </React.Fragment>
  );
};

export default SolicitudesAbm;
